import { useEffect, useRef, useState, useMemo, createRef, RefObject } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// Import components
import { isNil, isEmpty, first, cloneDeep, values, upperCase, keys, last, concat, noop, isArray } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
// Import Components
import { FormApplyAndResetButtonContainer } from './components';
import SingleDropdownForm from './SingleDropdownForm';
import MultiselectDropdownForm, { type MultiselectDropdownFormRef } from './MultiselectDropdownForm';
import SelectedItemsContainer from 'components/common/SelectedItemsContainer';
// Import UI Components
import { SkeletonLoader } from '@geneui/components';
// Import Configs
import { createFormDataObject } from './config';
// Import Hooks
import useRequest from 'hooks/UseRequest';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Constants
import { CascadeUITypes, CascadeRequestTypes, l } from 'constants/common';
import SimpleInputForm from './SimpleInputForm';
import SimpleMenuItemDropdownForm from './SimpleMenuItemDropdownForm';

// Returns a fresh request object every time
const getFreshDataByIdRequest = () => UtilsHttpService.getDataById();
const getFreshDataByIdsRequest = () => UtilsHttpService.getDataByIds();

const CascadeForm = ({
    handleApply,
    setFormData,
    data,
    filterList,
    withFormApplyAndResetButtonContainer,
    validateOnChange,
    getValidationUpdate,
}) => {
    const { t } = useTranslation();
    const { doGetRequest, doPostRequest } = useRequest();
    const formDataObject = createFormDataObject(false);

    const { Cascade, Restore } = useMemo(() => {
        const tmpData = JSON.parse(data.comparison.source);
        return { Cascade: isNil(tmpData?.Cascade) ? [] : tmpData.Cascade, Restore: tmpData?.Restore };
    }, [data.comparison.source]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function getCascadeInitialValue<T = any>(value: T, cascade = Cascade): T[] {
        return cascade?.map(() => value) ?? [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [cascadeData, setCascadeData] = useState(getCascadeInitialValue([] as any[]));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [cascadeValues, setCascadeValues] = useState(getCascadeInitialValue([] as any[]));
    const [isLoading, setIsLoading] = useState(getCascadeInitialValue(false));
    const [isDisabled, setIsDisabled] = useState(getCascadeInitialValue(true));
    const [resetHash, setResetHash] = useState<Nullable<string>>(null);

    const inFlightRequestsRef = useRef<{ cancel: (reason?: string) => void }[]>([]);

    const validate = () => {
        if (validateOnChange && !isEmpty(dropdownRef.current)) {
            Promise.all(
                dropdownRef.current.map((item) => {
                    if (!isNil(item?.current?.submitForm)) {
                        return item.current?.submitForm();
                    }
                    return new Promise((resolve) => {
                        resolve(false);
                        return;
                    });
                }),
            ).then((results) => {
                const isValid = results.reduce((acc, item) => acc && item, true);
                getValidationUpdate(isValid);
            });
        }
    };

    const SetCascadeData = (data, index) => {
        cascadeDataRef[index] = data;
        setCascadeData((prev) => {
            const result = [...prev];
            result[index] = cloneDeep(cascadeDataRef[index]);
            return result;
        });
    };

    const SetIsLoading = (index, value) => {
        setIsLoading((prev) => {
            const result = [...prev];
            result[index] = value;
            return result;
        });
    };

    const SetCascadeValues = (value, index, changeCascadeValuesState = true) => {
        cascadeValuesRef[index] = value;
        if (changeCascadeValuesState) {
            setCascadeValues((prev) => {
                const tmpValues = [...prev];
                tmpValues[index] = cloneDeep(cascadeValuesRef[index]);
                return tmpValues;
            });
        }
    };

    const dropdownRef = useRef<RefObject<MultiselectDropdownFormRef>[]>(Cascade.map(() => createRef()));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cascadeValuesRef = useRef(getCascadeInitialValue([] as any[])).current;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cascadeDataRef = useRef(getCascadeInitialValue([] as any[])).current;

    const getCascadeRootIndexes = (cascade = Cascade) => {
        const configIndexes: number[] = [];
        cascade.forEach((item, index) => {
            if (isNil(item.Params) || isEmpty(item.Params)) {
                configIndexes.push(index);
            }
        });
        return configIndexes;
    };

    const isItemDisabled = (config, cascadeValues = cascadeValuesRef) => {
        const params = getRequestParams(config, cascadeValues);
        let result = false;
        if (!isNil(params)) {
            result = keys(params).reduce((acc, item) => {
                acc = acc || isEmpty(params[item]);
                return acc;
            }, false);
        }
        return result;
    };

    const init = () => {
        // TODO: Review if `init` is still needed. Current resetHash effect may fully replace it. And actually this was the main reason why the requests got cancelled
        if (isNil(data.id)) {
            getCascadeRootIndexes().forEach((item) => {
                if (isNil(Cascade[item]?.Url)) return;
                getIncomingData(item).then((data) => {
                    SetCascadeData(data, item);
                });
            });

            setFormData(formDataObject);
        }
    };

    const cancelRequests = () => {
        inFlightRequestsRef.current.forEach((req) => req.cancel('Component unmounted or reset'));
        inFlightRequestsRef.current = [];
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        return () => {
            cancelRequests();
            setFormData(null);
        };
    }, []);

    useEffect(() => {
        if (isNil(data.id)) {
            resetHandler();
        }
    }, [data.comparison.comparison, data.id]);

    useEffect(() => {
        editDataSetter();
    }, [data.id]);

    useEffect(() => {
        setIsDisabled(() => Cascade.map((config) => isItemDisabled(config, cascadeValues)));
        setFormDataFromChild(cascadeValues);
        /*eslint react-hooks/exhaustive-deps:0*/
    }, [cascadeValues]);

    useEffect(() => {
        if (!isNil(resetHash)) {
            getCascadeRootIndexes().forEach((item) => {
                if (isEmpty(cascadeDataRef[item]) && !isNil(Cascade[item]?.Url)) {
                    getIncomingData(item).then((data) => {
                        SetCascadeData(data, item);
                    });
                }
            });
        }
    }, [resetHash]);

    const setRestoreCascadeValues = (data, CascadeFiltersList) => {
        const configIndex = data[0].configIndex;
        if (isNil(configIndex)) return;
        const isMenuItemInput = !isNil(Cascade[configIndex].MenuItemId);
        const isTextInput = [CascadeUITypes.Text, CascadeUITypes.Number].includes(Cascade[configIndex].UIType);

        const storedIds = CascadeFiltersList[configIndex].map((filter) =>
            isTextInput || isMenuItemInput ? filter.Id : +filter.Id,
        );

        data.forEach((item) => {
            const itemValue = isMenuItemInput || isTextInput ? item.value : +item.value;
            const foundInStoredIds = storedIds.includes(itemValue);
            if (foundInStoredIds && !cascadeValuesRef[item.configIndex].includes(itemValue)) {
                cascadeValuesRef[item.configIndex].push(itemValue);
            }
            if (!isNil(item?.Parents)) {
                item.Parents.forEach((parent) => {
                    setRestoreCascadeValues([parent], CascadeFiltersList);
                });
            }
        });
    };

    const editDataSetter = () => {
        const { id, parent } = data;
        if (!isNil(id)) {
            const { CascadeFiltersList } = filterList[parent.hashKey][id].Value.Data;
            const lastIndex = Cascade.length - 1;
            const isTextInput = [CascadeUITypes.Text, CascadeUITypes.Number].includes(Cascade[lastIndex].UIType);
            const isMenuItem = !isNil(Cascade[lastIndex].MenuItemId);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cascadeValuesRef[lastIndex] =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                last<any[]>(CascadeFiltersList)?.map((filter) => {
                    if (isMenuItem) {
                        return filter.Id;
                    }

                    if (isTextInput) {
                        return filter.Id;
                    }

                    return +filter.Id;
                }) ?? [];
            setIsLoading(getCascadeInitialValue(true));

            getIncomingData(lastIndex, false, Restore).then((data) => {
                cascadeValuesRef.forEach((_item, index) => (cascadeValuesRef[index] = []));
                setRestoreCascadeValues(data, CascadeFiltersList);
                let promise = Promise.resolve();
                Cascade.forEach((item, index) => {
                    promise = promise.then(() => {
                        if (!isItemDisabled(item)) {
                            return getIncomingData(index, false).then((data) => {
                                changeCascadeValues(data, index, false);
                                SetCascadeData(data, index);
                            });
                        } else {
                            SetCascadeValues([], index, false);
                            SetCascadeData([], index);
                        }
                    });
                });
                promise.then(() => {
                    setIsLoading(getCascadeInitialValue(false));
                    setCascadeValues(() => {
                        return cloneDeep(cascadeValuesRef);
                    });
                });
            });
        }
    };

    const getCascadeFilter = (item, configIndex) => {
        // dataIndex never equals -1
        const dataIndex = cascadeDataRef[configIndex].findIndex((dataItem) => dataItem.value === item);
        return { Id: item, DisplayName: cascadeDataRef[configIndex][dataIndex].label };
    };

    const setFormDataFromChild = (cascadeValues) => {
        const hasValue = !isNil(cascadeValues) && !isNil(last(cascadeValues));
        formDataObject.Data.Value = hasValue ? last(cascadeValues) : null;
        formDataObject.Data.CascadeFiltersList = hasValue
            ? cascadeValues.map((cascadeItem, index) => {
                  if (!isNil(Cascade[index]?.MenuItemId)) {
                      return cascadeItem;
                  }

                  if ([CascadeUITypes.Text, CascadeUITypes.Number].includes(Cascade[index]?.UIType)) {
                      return cascadeItem;
                  }

                  return cascadeItem.map((item) => {
                      return getCascadeFilter(item, index);
                  });
              })
            : null;
        setFormData(formDataObject);
    };

    const resetHandler = () => {
        cancelRequests();
        cascadeValuesRef.forEach((_item, index) => (cascadeValuesRef[index] = []));
        setCascadeValues(() => cloneDeep(cascadeValuesRef));
        setIsLoading(getCascadeInitialValue(false));
        setResetHash(uuidv4());
        setFormData(createFormDataObject(false));

        if (!isEmpty(dropdownRef.current)) {
            dropdownRef.current.forEach((item) => {
                if (!isNil(item?.current?.reset)) {
                    // reset forms which can reset
                    item.current?.reset();
                }
            });
        }
    };

    const applyHandler = () => {
        if (!isEmpty(dropdownRef.current)) {
            Promise.all(
                dropdownRef.current.map((item) => {
                    return item.current?.submitForm();
                }),
            ).then((results) => {
                const isValid = results.reduce((acc, item) => acc && item, true);
                if (isValid) {
                    handleApply();
                }
            });
        }
    };

    const getCascadeDependencyIndexes = (index) => {
        const configIndexes: number[] = [];
        Cascade.forEach((cascadeItem, cascadeIndex) => {
            if (!isNil(cascadeItem.Params)) {
                values(cascadeItem.Params).forEach((value) => {
                    if (value === index && configIndexes.findIndex((item) => item === cascadeIndex) === -1) {
                        configIndexes.push(cascadeIndex);
                    }
                });
            }
        });
        return configIndexes;
    };

    const getRecursiveCascadeDependencyIndexes = (index) => {
        const configIndexes = getCascadeDependencyIndexes(index);
        if (isEmpty(configIndexes)) {
            return configIndexes;
        }
        let result = configIndexes;
        result.forEach((item) => {
            result = concat(result, getRecursiveCascadeDependencyIndexes(item));
        });
        return result;
    };

    const changeCascadeValues = (data, configIndexes, changeCascadeValuesState = true) => {
        const value = cascadeValuesRef[configIndexes];
        const dataIdes = data.map((item) => item.value);
        const tmpValue = value.filter((item) => dataIdes.includes(item));
        SetCascadeValues(tmpValue, configIndexes, changeCascadeValuesState);
    };

    const setIncomingData = (index, clear = false) => {
        const dependIndexes = getRecursiveCascadeDependencyIndexes(index);
        setIsLoading((prev) => {
            const result = [...prev];
            dependIndexes.forEach((dependIndex) => {
                result[dependIndex] = !isItemDisabled(Cascade[dependIndex]);
            });
            return result;
        });

        let promise = Promise.resolve();
        dependIndexes.forEach((configIndex) => {
            promise = promise.then(() => {
                if (!clear && !isItemDisabled(Cascade[configIndex])) {
                    return getIncomingData(configIndex, false).then((data) => {
                        changeCascadeValues(data, configIndex);
                        SetCascadeData(data, configIndex);
                    });
                } else {
                    SetCascadeValues([], configIndex);
                    SetCascadeData([], configIndex);
                }
            });
        });
        promise.then(() => {
            setIsLoading((prev) => {
                const result = [...prev];
                dependIndexes.forEach((dependIndex) => {
                    result[dependIndex] = false;
                });
                return result;
            });
        });
    };

    const mapParentsData = (parentItem, config) => {
        const tmpConfig = Cascade[config.Params[parentItem.ParentName]];

        return {
            label: parentItem?.Name,
            value: +parentItem?.Id,
            configIndex: config.Params[parentItem.ParentName],
            ParentName: parentItem?.ParentName,
            Parents: isNil(parentItem.Parents)
                ? null
                : parentItem?.Parents.map((parentItem) => mapParentsData(parentItem, tmpConfig)),
        };
    };

    const mapTreeToDropDown = (treeData, index = 0, parentText = '') => {
        let result: { label: string; value: number }[] = [];
        treeData.forEach((item) => {
            const tmpLabel = index > 1 ? `${parentText} \\ ${item.text}` : item.text;
            result.push({ label: tmpLabel, value: item.ID });
            if (!isNil(item?.items)) {
                result = concat(result, mapTreeToDropDown(item.items, index + 1, tmpLabel));
            }
        });
        return result;
    };

    const mapApiModel = (data, configIndex) => {
        const config = Cascade[configIndex];

        if (!isNil(config?.MenuItemId)) {
            return data.map((value) => {
                return {
                    value: value,
                    configIndex: configIndex,
                    ParentName: null,
                    Parents: null,
                };
            });
        }

        if ([CascadeUITypes.Text, CascadeUITypes.Number].includes(config?.UIType)) {
            return data.map((value) => {
                return {
                    value: value,
                    configIndex: configIndex,
                    ParentName: null,
                    Parents: null,
                };
            });
        }

        if (config?.UIType === CascadeUITypes.DropDown) {
            return data.map(({ Name, Id, Parents, ParentName }) => {
                return {
                    label: Name,
                    value: +Id,
                    configIndex: configIndex,
                    ParentName: ParentName,
                    Parents: isNil(Parents) ? null : Parents.map((parentItem) => mapParentsData(parentItem, config)),
                }; // all dropdown data must be number
            });
        }
        return mapTreeToDropDown(data).map((item) => ({ ...item, configIndex: configIndex }));
    };

    const getRequestParams = (config, cascadeValues = cascadeValuesRef) => {
        const tmpParams = isNil(config.Params) ? {} : config.Params;
        const result = keys(tmpParams).reduce((acc, item) => {
            acc[item] = cascadeValues[tmpParams[item]]?.map((value) => +value); // for Tree case value?.ID, now tree converted to DropDown
            return acc;
        }, {});
        return result;
    };

    const getIncomingData = (index, withLoading = true, configExtender = {}) => {
        if (!isNil(Cascade[index].MenuItemId)) {
            return Promise.resolve(mapApiModel(cascadeValuesRef[index], index));
        }

        if ([CascadeUITypes.Text, CascadeUITypes.Number].includes(Cascade[index].UIType)) {
            return Promise.resolve(mapApiModel(cascadeValuesRef[index], index));
        }

        return new Promise((resolve, reject) => {
            const config = { ...Cascade[index], ...configExtender };
            if (!isNil(config)) {
                if (withLoading) {
                    SetIsLoading(index, true);
                }
                if (upperCase(config.Method) === CascadeRequestTypes.GET) {
                    // TODO: get with params does not supported

                    const requestObj = getFreshDataByIdRequest(); // fresh request
                    inFlightRequestsRef.current.push(requestObj); // track it
                    doGetRequest(requestObj.request, {
                        queryString: {
                            url: config.Url,
                        },
                        successCallback: (data) => {
                            // filter this request from the inFlight reqeusts list
                            inFlightRequestsRef.current = inFlightRequestsRef.current.filter((r) => r !== requestObj);
                            if (withLoading) {
                                SetIsLoading(index, false);
                            }
                            resolve(mapApiModel(data, index));
                            return;
                        },
                    });
                } else if (upperCase(config.Method) === CascadeRequestTypes.POST) {
                    const tmpRequestBody = getRequestParams(config);
                    if (!isNil(tmpRequestBody)) {
                        const requestObj = getFreshDataByIdsRequest();
                        inFlightRequestsRef.current.push(requestObj); // track it

                        doPostRequest(requestObj.request, {
                            queryString: { url: config.Url },
                            requestBody: tmpRequestBody,
                            successCallback: (data) => {
                                // filter this request from the inFlight reqeusts list
                                inFlightRequestsRef.current = inFlightRequestsRef.current.filter(
                                    (r) => r !== requestObj,
                                );
                                if (withLoading) {
                                    SetIsLoading(index, false);
                                }

                                resolve(mapApiModel(data, index));
                                return;
                            },
                        });
                    }
                }
            } else {
                reject();
                return;
            }
        });
    };

    const singleDropdownChangeHandler = (index) => {
        return (e) => {
            if (isNil(e?.value)) {
                SetCascadeValues([], index);
                setIncomingData(index, true);
            } else {
                SetCascadeValues([e.value], index);
                setIncomingData(index);
            }
        };
    };

    const multiselectDropDownChangeHandler = (index) => {
        return (e) => {
            SetCascadeValues(e, index);
        };
    };

    const menuItemChangeHandler = (index) => {
        return (e) => {
            SetCascadeValues(e?.value ?? [], index);
            SetCascadeValues(isNil(e?.value) ? [] : isArray(e?.value) ? e?.value : [e?.value], index);
        };
    };

    const simpleInputChangeHandler = (index) => {
        return (e) => {
            if (isNil(e?.target?.value)) {
                SetCascadeValues([], index);
                setIncomingData(index, true);
            } else {
                SetCascadeValues([e.target.value], index);
                setIncomingData(index);
            }
        };
    };

    const multiselectDropDownCloseHandler = (index) => {
        return () => {
            setIncomingData(index);
        };
    };

    // TODO: uncomment when added tree
    // const singleTreeDropDownChangeHandler = (index) => {
    //     return (e) => {
    //         SetCascadeValues([{ ID: e.ID, text: e.text }], index);
    //         setIncomingData(index);
    //     };
    // };

    const getParentsLabels = (configIndex, value) => {
        let result: string[] = [];
        const dataIndex = cascadeDataRef[configIndex].findIndex((item) => item.value === value);
        if (dataIndex !== -1) {
            const currentItem = cascadeDataRef[configIndex][dataIndex];
            result.push(currentItem.label);
            if (!isNil(currentItem.Parents)) {
                currentItem.Parents.forEach((parentItem) => {
                    result = concat(result, getParentsLabels(parentItem.configIndex, parentItem.value));
                });
            }
        }
        return result;
    };

    const getSelectedItems = (data, values) => {
        const result = data.filter(({ value }) => values.includes(value));
        return result.map((item) => {
            const labels = getParentsLabels(item.configIndex, item.value);
            const tmpTooltip = labels.reduce((acc, item) => {
                acc = `${item}\n${acc}`;
                return acc;
            }, '');
            return { value: item.value, label: item.label, tooltip: tmpTooltip };
        });
    };

    const selectedItemsRemoveHandler = (e) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tmpValue = last<any[]>(cascadeValuesRef)?.filter((item) => item !== e.value);
        SetCascadeValues(tmpValue, Cascade.length - 1);
    };

    const isDisabledApply = (isLoading) => {
        return isLoading.reduce((acc, item) => {
            acc = acc || item;
            return acc;
        }, false);
    };

    const timeoutKey = useRef<Nullable<NodeJS.Timeout>>(null);
    useEffect(() => {
        if (validateOnChange) {
            if (timeoutKey.current) {
                clearTimeout(timeoutKey.current);
            }
            timeoutKey.current = setTimeout(validate, 0);
        }
    }, [cascadeValues, isLoading]);

    return (
        <>
            <div className="crm-dynamic-segment-form-content">
                <div className="crm-scroll-holder">
                    <div className="cascade-form">
                        <div
                            className="crm-dynamic-segment-form__content
                    crm-flex crm-sports-book-match-form
                    crm-justify-content-between
                    cascade-form-items"
                        >
                            {Cascade.map((item, index) => {
                                return (
                                    <div
                                        key={index}
                                        className="crm-sports-book-match-form__dropdown-with-icon cascade-form-item"
                                    >
                                        <SkeletonLoader key={index} isBusy={isLoading[index]}>
                                            <>
                                                {isNil(item.MenuItemId) &&
                                                    [CascadeUITypes.DropDown, CascadeUITypes.Tree].includes(
                                                        item.UIType,
                                                    ) &&
                                                    item.IsMultiselect && (
                                                        <MultiselectDropdownForm
                                                            ref={dropdownRef.current[index]}
                                                            isShowLabelWithId={true}
                                                            key={index}
                                                            disabled={isDisabled[index]}
                                                            hasSearch={true}
                                                            inputSize="default"
                                                            labelAppearance="swap"
                                                            value={cascadeValues[index]}
                                                            data={cascadeData[index]}
                                                            onChange={multiselectDropDownChangeHandler(index)}
                                                            onClose={multiselectDropDownCloseHandler(index)}
                                                            searchPlaceholderText={t(l.Search)}
                                                            noDataText={t(l.NoDataFound)}
                                                            placeholder={t(item.Label)}
                                                        />
                                                    )}
                                                {isNil(item.MenuItemId) &&
                                                    [CascadeUITypes.DropDown, CascadeUITypes.Tree].includes(
                                                        item.UIType,
                                                    ) &&
                                                    !item.IsMultiselect && (
                                                        <SingleDropdownForm
                                                            key={index}
                                                            disabled={isDisabled[index]}
                                                            hasSearch={true}
                                                            inputSize="default"
                                                            labelAppearance="swap"
                                                            value={first(cascadeValues[index])}
                                                            data={cascadeData[index]}
                                                            onChange={singleDropdownChangeHandler(index)}
                                                            searchPlaceholderText={t(l.Search)}
                                                            noDataText={t(l.NoDataFound)}
                                                            placeholder={t(item.Label)}
                                                            clearable={!!item.Clearable}
                                                            ref={dropdownRef.current[index]}
                                                        />
                                                    )}
                                                {isNil(item.MenuItemId) && item.UIType === CascadeUITypes.Text && (
                                                    <SimpleInputForm
                                                        key={index}
                                                        placeholder={t(item.Label)}
                                                        value={first(cascadeValues[index])}
                                                        onChange={simpleInputChangeHandler(index)}
                                                        ref={dropdownRef.current[index]}
                                                    />
                                                )}
                                                {!isNil(item.MenuItemId) && (
                                                    <SimpleMenuItemDropdownForm
                                                        value={cascadeValues[index]}
                                                        menuItemId={item.MenuItemId}
                                                        onChange={menuItemChangeHandler(index)}
                                                        displayName={t(item.Label)}
                                                        placeholder={t(item.Label)}
                                                        isMultiselect={item.IsMultiselect}
                                                        ref={dropdownRef.current[index]}
                                                    />
                                                )}
                                            </>
                                        </SkeletonLoader>
                                    </div>
                                );
                            })}
                        </div>

                        {withFormApplyAndResetButtonContainer && last<AnyObject>(Cascade)?.IsMultiselect && (
                            <SelectedItemsContainer
                                className="cascade-selected-items-container"
                                data={getSelectedItems(last(cascadeData), last(cascadeValues))}
                                removable={true}
                                onRemove={selectedItemsRemoveHandler}
                                isLoading={last(isLoading) && !isEmpty(last(cascadeValuesRef))}
                                withTooltip={true}
                            />
                        )}
                    </div>
                </div>
            </div>
            {withFormApplyAndResetButtonContainer && (
                <FormApplyAndResetButtonContainer
                    handleApply={applyHandler}
                    handleReset={resetHandler}
                    disabledApply={isDisabledApply(isLoading)}
                />
            )}
        </>
    );
};

CascadeForm.propTypes = {
    setFormData: PropTypes.func,
    handleApply: PropTypes.func,
    setError: PropTypes.func,
    data: PropTypes.object,
    filterList: PropTypes.object,
    setValidation: PropTypes.func,
    validation: PropTypes.object,
    withFormApplyAndResetButtonContainer: PropTypes.bool,
    validateOnChange: PropTypes.bool,
    getValidationUpdate: PropTypes.func,
};

CascadeForm.defaultProps = {
    withFormApplyAndResetButtonContainer: true,
    validateOnChange: false,
    getValidationUpdate: noop,
};

export default CascadeForm;
